import React, { PropsWithChildren, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import { useParams, NavLink } from 'react-router-dom'
import { Form, Formik } from 'formik'
import {
  faChevronDown,
  faSearch,
  faDownload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'

import Button from 'components/Button'
import Dropdown, { OptionType } from 'components/Dropdown'

import { useCanUI } from 'hooks/useCanUI'
import { TRANSACTION_TYPES } from 'constants/filters'
import { formatDateToShort } from 'services/helpers'
import { DropDownItem } from 'types/components'

import useTransactionsFilter from './useTransactionsFilter'
import useTransactionsMerchantLookup from './useTransactionsMerchantLookup'

type DatePickerProps = React.InputHTMLAttributes<HTMLInputElement> & {
  value?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

function Transactions(): React.ReactElement {
  const { isMemberTransactionDownload } = useCanUI()
  const { traderId } = useParams()
  const { merchantLookupDropdownOptions } = useTransactionsMerchantLookup({
    memberTraderId: String(traderId),
  })

  const {
    handleTransactionFilter,
    handleTransactionFilterClear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setSelectedMerchant,
    initialValues,
    isFilteredTransactions,
    transactionsLoading,
    transactionTypeId,
    merchantId,
  } = useTransactionsFilter({ memberTraderId: String(traderId) })

  const onChangeDatepicker = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const formatDate = (): string => {
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
    }
    if (startDate) {
      return `${startDate.toLocaleDateString()}`
    }
    return ''
  }

  const DatePickerInput = ({ onClick }: PropsWithChildren<DatePickerProps>) => (
    <button
      className="p-2 pr-10 text-left border border-gray-400"
      style={{ minWidth: '120px' }}
      onClick={onClick}
      type="button"
    >
      {formatDate()}
      <Icon
        icon={faChevronDown}
        className="absolute top-0 right-0 h-full ml-4 mr-2"
      />
    </button>
  )

  const merchantLoadOptions = useCallback(
    async (inputValue: string): Promise<DropDownItem[]> =>
      merchantLookupDropdownOptions(inputValue),
    [merchantLookupDropdownOptions]
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleTransactionFilter}>
      {({ values, setFieldValue, submitForm }) => {
        const downloadSearchParams = new URLSearchParams()
        if (startDate && endDate) {
          downloadSearchParams.set('startDate', formatDateToShort(startDate))
          downloadSearchParams.set('endDate', formatDateToShort(endDate))
        }
        if (transactionTypeId)
          downloadSearchParams.set('transactionTypeId', String(transactionTypeId))
        if (merchantId) downloadSearchParams.set('merchantId', String(merchantId))
        if (values.customerTransactionsOnly)
          downloadSearchParams.set('customerTransactionsOnly', 'true')

        return (
          <Form>
            <div className="flex flex-wrap items-end mt-4 mb-2 gap-2">
              <Dropdown
                title="Merchants"
                value={values.merchant}
                loadOptions={merchantLoadOptions}
                onChange={(option: OptionType) => {
                  if (option) {
                    setSelectedMerchant(option)
                    setFieldValue('merchant', option.value)
                  } else {
                    setSelectedMerchant(null)
                    setFieldValue('merchant', '')
                  }
                }}
                className="w-80"
                borderColor="#C4C4C4"
                containerClassName="focus:border-primary"
                placeholder="Search by merchant name..."
                isClearable
                isSearchable
                async
              />

              <div className="flex flex-col gap-2">
                <p>
                  <span className="tracking-wider font-light">Date Range</span>
                </p>
                <DatePicker
                  title="Date Picker"
                  name="dateRange"
                  onChange={onChangeDatepicker}
                  startDate={startDate}
                  endDate={endDate}
                  className="p-2 border border-gray-400"
                  customInput={<DatePickerInput />}
                  selectsRange
                  isClearable={false}
                  showYearDropdown
                  scrollableYearDropdown
                />
              </div>

              <Dropdown
                title="Transaction Type"
                value={values.transactionType}
                options={TRANSACTION_TYPES}
                onSelect={(option: DropDownItem) =>
                  setFieldValue('transactionType', option)
                }
                className="w-40"
                borderColor="#C4C4C4"
              />

              <Button
                type="submit"
                icon={faSearch}
                className="ml-1 rounded-lg"
                iconRight={false}
                disabled={transactionsLoading}
              >
                <span className="tracking-wider font-bold">Filter</span>
              </Button>

              {isMemberTransactionDownload && (
                <Button
                  type="button"
                  icon={faDownload}
                  className="ml-1 rounded-lg"
                  iconRight={false}
                >
                  <NavLink
                    to={`/reports/${traderId}/member-transactions-reports?${downloadSearchParams}`}
                  >
                    <span className="tracking-wider font-bold">Download</span>
                  </NavLink>
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 mb-2">
              <input
                id="customer-transactions-only"
                name="customerTransactionsOnly"
                type="checkbox"
                checked={values.customerTransactionsOnly}
                onChange={(event) => {
                  setFieldValue('customerTransactionsOnly', event.target.checked)
                  submitForm()
                }}
                className="h-4 w-4 rounded border-gray-300"
                style={{
                  accentColor: '#1e355e',
                } as React.CSSProperties}
              />
              <label
                htmlFor="customer-transactions-only"
                className="text-sm font-medium text-gray-700"
              >
                As From Trader Transactions
              </label>
              {isFilteredTransactions && (
                <Button
                  type="reset"
                  className="rounded-lg"
                  iconRight={false}
                  onClick={() => {
                    setFieldValue('customerTransactionsOnly', false)
                    handleTransactionFilterClear()
                  }}
                  disabled={transactionsLoading}
                >
                  <span className="tracking-wider font-bold">Clear</span>
                </Button>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default Transactions
